import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Kart, KartGovde, Rozet } from '@/components/ui/card';
import { Alan, Secim } from '@/components/ui/field';
import { ApiHatasi, api } from '@/lib/api';
import { paraFormatla, tarihFormatla } from '@/lib/utils';

/**
 * Gelen kutusu: OTA'dan e-posta ile dusen rezervasyon adaylari.
 *
 * Kayitlar KENDILIGINDEN rezervasyona donmez. Ayristirici alanlari on
 * doldurur, gorevli bakar, gerekirse duzeltir ve onaylar. Sebep iki yonlu:
 * e-posta bicimleri kanaldan kanala degisir ve e-posta taklit edilebilir.
 * Yanlis okunan bir tarihle sessizce acilan rezervasyon, odayi kimsenin
 * fark etmedigi bicimde bloke ederdi.
 *
 * "Guven" yuzdesi ayristiricinin kac kritik alani bulabildigini soyler.
 * Dusukse satir sari isaretlenir - gorevli goz ucuyla onaylamasin diye.
 */

interface Kayit {
  id: string;
  kanal: string;
  gonderen: string;
  konu: string | null;
  harciKod: string | null;
  misafirAd: string | null;
  misafirSoyad: string | null;
  girisTarihi: string | null;
  cikisTarihi: string | null;
  yetiskin: number | null;
  cocuk: number | null;
  odaTipi: string | null;
  toplamKurus: number | null;
  guven: number;
  durum: 'BEKLIYOR' | 'ISLENDI' | 'YOKSAYILDI';
  reservationId: string | null;
}

interface OdaTipi {
  id: string;
  ad: string;
}

const KANAL_ADI: Record<string, string> = {
  ETSTUR: 'Etstur',
  ODAMAX: 'Odamax',
  OTELZ: 'Otelz',
  TATILBUDUR: 'Tatilbudur',
  TATILSEPETI: 'Tatilsepeti',
  JOLLY: 'Jolly Tur',
  SETUR: 'Setur',
  GEZINOMI: 'Gezinomi',
  BOOKING: 'Booking.com',
  AIRBNB: 'Airbnb',
  BILINMIYOR: 'Bilinmeyen kanal',
};

export function GelenKutusu() {
  const [kayitlar, setKayitlar] = React.useState<Kayit[]>([]);
  const [tipler, setTipler] = React.useState<OdaTipi[]>([]);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [acik, setAcik] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    try {
      const bugun = new Date().toISOString().slice(0, 10);
      const yarin = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
      const [gelen, plan] = await Promise.all([
        api<{ kayitlar: Kayit[] }>('/api/gelen-rezervasyonlar'),
        api<{ odalar: { roomTypeId: string; roomTypeAd: string | null }[] }>(
          `/api/rezervasyonlar/oda-plani?baslangic=${bugun}&bitis=${yarin}`,
        ).catch(() => ({ odalar: [] })),
      ]);
      setKayitlar(gelen.kayitlar);
      const benzersiz = new Map<string, string>();
      for (const o of plan.odalar) if (o.roomTypeId) benzersiz.set(o.roomTypeId, o.roomTypeAd ?? o.roomTypeId);
      setTipler([...benzersiz].map(([id, ad]) => ({ id, ad })));
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Gelen kutusu alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  const bekleyenler = kayitlar.filter((k) => k.durum === 'BEKLIYOR');
  const digerleri = kayitlar.filter((k) => k.durum !== 'BEKLIYOR');

  return (
    <main className="mx-auto flex w-full max-w-5xl flex-col gap-5 p-4 sm:p-7">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-[-.02em] sm:text-3xl">Gelen kutusu</h1>
          <p className="mt-1 text-[15px] text-gri">
            Kanaldan e-posta ile düşen rezervasyonlar. Siz onaylamadan oda bloke edilmez.
          </p>
        </div>
        {bekleyenler.length > 0 && <Rozet renk="uyari">{bekleyenler.length} bekliyor</Rozet>}
      </header>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      {yukleniyor ? (
        <div className="h-24 animate-pulse rounded-2xl bg-white" />
      ) : kayitlar.length === 0 ? (
        <Kart>
          <KartGovde>
            <p className="font-semibold">Henüz e-posta gelmedi.</p>
            <p className="mt-1.5 max-w-[70ch] text-[14px] text-gri">
              Kanal rezervasyon e-postalarınızı tesis adresinize yönlendirin; buraya düşsün. Adresi ve
              hangi göndericilere güvenileceğini biz tanımlıyoruz — bize yazmanız yeterli.
            </p>
          </KartGovde>
        </Kart>
      ) : (
        <div className="flex flex-col gap-3">
          {[...bekleyenler, ...digerleri].map((k) => (
            <Satir
              key={k.id}
              kayit={k}
              tipler={tipler}
              acik={acik === k.id}
              onAc={() => setAcik(acik === k.id ? null : k.id)}
              onDegisti={yukle}
            />
          ))}
        </div>
      )}
    </main>
  );
}

function Satir({
  kayit,
  tipler,
  acik,
  onAc,
  onDegisti,
}: {
  kayit: Kayit;
  tipler: OdaTipi[];
  acik: boolean;
  onAc: () => void;
  onDegisti: () => void;
}) {
  const [form, setForm] = React.useState({
    roomTypeId: '',
    girisTarihi: kayit.girisTarihi ?? '',
    cikisTarihi: kayit.cikisTarihi ?? '',
    ad: kayit.misafirAd ?? '',
    soyad: kayit.misafirSoyad ?? '',
    yetiskin: kayit.yetiskin ?? 1,
    cocuk: kayit.cocuk ?? 0,
    toplamKurus: kayit.toplamKurus ?? 0,
  });
  const [islemde, setIslemde] = React.useState(false);
  const [satirHata, setSatirHata] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!form.roomTypeId && tipler[0]) setForm((f) => ({ ...f, roomTypeId: tipler[0]!.id }));
  }, [tipler, form.roomTypeId]);

  const bekliyor = kayit.durum === 'BEKLIYOR';
  const suphe = kayit.guven < 100;

  async function onayla() {
    setIslemde(true);
    setSatirHata(null);
    try {
      await api(`/api/gelen-rezervasyonlar/${kayit.id}/onayla`, {
        method: 'POST',
        body: JSON.stringify({ ...form, toplamKurus: Number(form.toplamKurus) || 0 }),
      });
      onDegisti();
    } catch (e) {
      setSatirHata(e instanceof ApiHatasi ? e.message : 'Onaylanamadı.');
    } finally {
      setIslemde(false);
    }
  }

  async function yoksay() {
    setIslemde(true);
    try {
      await api(`/api/gelen-rezervasyonlar/${kayit.id}/yoksay`, { method: 'POST' });
      onDegisti();
    } catch (e) {
      setSatirHata(e instanceof ApiHatasi ? e.message : 'İşlenemedi.');
      setIslemde(false);
    }
  }

  return (
    <Kart>
      <KartGovde>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Rozet renk="bilgi">{KANAL_ADI[kayit.kanal] ?? kayit.kanal}</Rozet>
          <span className="font-semibold">
            {[kayit.misafirAd, kayit.misafirSoyad].filter(Boolean).join(' ') || 'Ad okunamadı'}
          </span>
          <span className="text-[14px] text-gri">
            {kayit.girisTarihi ? tarihFormatla(kayit.girisTarihi) : '—'} /{' '}
            {kayit.cikisTarihi ? tarihFormatla(kayit.cikisTarihi) : '—'}
          </span>
          {kayit.toplamKurus != null && kayit.toplamKurus > 0 && (
            <span className="text-[14px] font-semibold">{paraFormatla(kayit.toplamKurus)}</span>
          )}
          {bekliyor && suphe && <Rozet renk="uyari">Kontrol edin · %{kayit.guven}</Rozet>}
          {kayit.durum === 'ISLENDI' && <Rozet renk="basari">Rezervasyona çevrildi</Rozet>}
          {kayit.durum === 'YOKSAYILDI' && <Rozet>Yoksayıldı</Rozet>}

          {bekliyor && (
            <Button varyant="ikincil" boyut="md" className="ml-auto" onClick={onAc}>
              {acik ? 'Kapat' : 'İncele ve onayla'}
            </Button>
          )}
        </div>

        {acik && bekliyor && (
          <div className="mt-4 flex flex-col gap-4 border-t border-slate-200 pt-4">
            {suphe && (
              <p className="rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-[13.5px] text-amber-900">
                Bazı alanlar e-postadan okunamadı. Onaylamadan önce tarihleri ve adı kontrol edin.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Alan etiket="Ad" value={form.ad} onChange={(e) => setForm({ ...form, ad: e.target.value })} />
              <Alan etiket="Soyad" value={form.soyad} onChange={(e) => setForm({ ...form, soyad: e.target.value })} />
              <Alan
                etiket="Giriş"
                type="date"
                value={form.girisTarihi}
                onChange={(e) => setForm({ ...form, girisTarihi: e.target.value })}
              />
              <Alan
                etiket="Çıkış"
                type="date"
                value={form.cikisTarihi}
                onChange={(e) => setForm({ ...form, cikisTarihi: e.target.value })}
              />
              <Secim
                etiket="Oda tipi"
                value={form.roomTypeId}
                onChange={(e) => setForm({ ...form, roomTypeId: e.target.value })}
                secenekler={tipler.map((t) => ({ deger: t.id, metin: t.ad }))}
              />
              <Alan
                etiket="Toplam (kuruş)"
                type="number"
                value={String(form.toplamKurus)}
                onChange={(e) => setForm({ ...form, toplamKurus: Number(e.target.value) })}
              />
            </div>

            {satirHata && <p className="text-[13.5px] text-red-700">{satirHata}</p>}

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={onayla} yukleniyor={islemde} disabled={!form.ad || !form.soyad || !form.roomTypeId}>
                Rezervasyona çevir
              </Button>
              <Button varyant="sessiz" onClick={yoksay} disabled={islemde}>
                Yoksay
              </Button>
              <span className="ml-auto text-[12.5px] text-gri">
                {kayit.harciKod ? `Kanal kodu: ${kayit.harciKod}` : 'Kanal kodu okunamadı'}
              </span>
            </div>
          </div>
        )}
      </KartGovde>
    </Kart>
  );
}
